import * as maplibregl from 'maplibre-gl';
import { API_URL_ADMIN } from '~/config/config';

export const COLLECT_PHOTO_SOURCE_ID = 'collect-photo-tiles-source';
export const COLLECT_PHOTO_LAYER_ID_POINT = 'collect-photo-layer-point';
export const COLLECT_PHOTO_LAYER_ID_LABEL = 'collect-photo-layer-label';
export const COLLECT_PHOTO_SOURCE_LAYER = 'collect_item_photo';

interface CollectPhotoPopupOptions {
  lngLat: { lng: number; lat: number };
  properties: Record<string, any>;
}

let collectPhotoEvents: Record<string, any> = {};
let currentProjectId: number | undefined = undefined;

const CollectPhotoVectorTileUtils = {
  addToMap: (projectId?: number) => {
    const map = window.map;
    if (!map) return;

    currentProjectId = projectId;

    // Remove existing layers and source
    CollectPhotoVectorTileUtils._removeLayers();

    if (map.getSource(COLLECT_PHOTO_SOURCE_ID)) {
      map.removeSource(COLLECT_PHOTO_SOURCE_ID);
    }

    // Build tile URL
    const tileUrl = projectId
      ? `${API_URL_ADMIN}/api/geocollect/tiles/collect-item-photo/{z}/{x}/{y}.pbf?project_id=${projectId}`
      : `${API_URL_ADMIN}/api/geocollect/tiles/collect-item-photo/{z}/{x}/{y}.pbf`;

    map.addSource(COLLECT_PHOTO_SOURCE_ID, {
      type: 'vector',
      tiles: [tileUrl],
      minzoom: 0,
      maxzoom: 22,
    });

    CollectPhotoVectorTileUtils._addLayers();
    CollectPhotoVectorTileUtils._addEventListeners();
  },

  _addLayers: () => {
    const map = window.map;
    if (!map) return;

    // Circle point layer (green — collected photos)
    map.addLayer({
      id: COLLECT_PHOTO_LAYER_ID_POINT,
      type: 'circle',
      source: COLLECT_PHOTO_SOURCE_ID,
      'source-layer': COLLECT_PHOTO_SOURCE_LAYER,
      minzoom: 8,
      paint: {
        'circle-color': '#22c55e',
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8, 3,
          12, 5,
          16, 9,
          20, 12,
        ],
        'circle-stroke-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8, 1,
          14, 2,
        ],
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.9,
      },
    });

    // Label layer (shown only at zoom >= 14)
    map.addLayer({
      id: COLLECT_PHOTO_LAYER_ID_LABEL,
      type: 'symbol',
      source: COLLECT_PHOTO_SOURCE_ID,
      'source-layer': COLLECT_PHOTO_SOURCE_LAYER,
      minzoom: 14,
      layout: {
        'text-field': ['get', 'uuid'],
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-size': 9,
        'text-offset': [0, 1.6],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'text-max-width': 8,
      },
      paint: {
        'text-color': '#15803d',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1,
      },
    });
  },

  _removeLayers: () => {
    const map = window.map;
    if (!map) return;

    const layerIds = [COLLECT_PHOTO_LAYER_ID_POINT, COLLECT_PHOTO_LAYER_ID_LABEL];
    layerIds.forEach((id) => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
  },

  moveLayerToTop: () => {
    const map = window.map;
    if (!map) return;

    [COLLECT_PHOTO_LAYER_ID_LABEL, COLLECT_PHOTO_LAYER_ID_POINT].forEach((id) => {
      if (map.getLayer(id)) {
        map.moveLayer(id);
      }
    });
  },

  _addEventListeners: () => {
    const map = window.map;
    if (!map) return;

    const onMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    const onClick = (e: any) => {
      if (!e.features || e.features.length === 0) return;
      const feature = e.features[0];
      const props = feature.properties || {};
      const lngLat = e.lngLat;

      CollectPhotoVectorTileUtils._showPopup({ lngLat, properties: props });
    };

    map.on('mouseenter', COLLECT_PHOTO_LAYER_ID_POINT, onMouseEnter);
    map.on('mouseleave', COLLECT_PHOTO_LAYER_ID_POINT, onMouseLeave);
    map.on('click', COLLECT_PHOTO_LAYER_ID_POINT, onClick);

    collectPhotoEvents = { onMouseEnter, onMouseLeave, onClick };
  },

  _showPopup: ({ lngLat, properties }: CollectPhotoPopupOptions) => {

    // Remove existing popup if any
    const existingPopup = (window as any).__collectPhotoPopup;
    if (existingPopup) {
      existingPopup.remove();
    }

    const dateStr = properties.created_at
      ? new Date(properties.created_at).toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

    // For collect item files, we download thumbnail via file uuid
    const thumbnailUrl = properties.uuid
      ? `${API_URL_ADMIN}/api/geocollect/restore/download-file/${properties.uuid}`
      : '';

    const popupHTML = `
      <div style="min-width:180px;max-width:220px;font-family:sans-serif;">
        ${thumbnailUrl ? `
          <div style="width:100%;height:130px;overflow:hidden;border-radius:6px 6px 0 0;margin:-12px -12px 8px -12px;width:calc(100% + 24px);">
            <img
              src="${thumbnailUrl}"
              style="width:100%;height:100%;object-fit:cover;display:block;"
              onerror="this.parentElement.style.display='none'"
            />
          </div>
        ` : ''}
        <div style="display:flex;align-items:center;gap:4px;margin-bottom:4px;">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#22c55e"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <span style="font-size:11px;color:#374151;font-weight:500;">Ảnh thu thập</span>
        </div>
        ${dateStr ? `
          <div style="font-size:10px;color:#64748b;">
            <span>&#128197; ${dateStr}</span>
          </div>
        ` : ''}
        ${properties.collect_item_uuid ? `
          <div style="font-size:10px;color:#94a3b8;margin-top:2px;">
            <span>Item: ${String(properties.collect_item_uuid).slice(0, 8)}...</span>
          </div>
        ` : ''}
      </div>
    `;

    if (!window.map) return;
    const popup = new maplibregl.Popup({ maxWidth: '240px', closeButton: true, offset: 12 })
      .setLngLat([lngLat.lng, lngLat.lat])
      .setHTML(popupHTML)
      .addTo(window.map);

    (window as any).__collectPhotoPopup = popup;
  },

  removeFromMap: () => {
    const map = window.map;
    if (!map) return;

    // Remove event listeners
    if (collectPhotoEvents.onMouseEnter) {
      map.off('mouseenter', COLLECT_PHOTO_LAYER_ID_POINT, collectPhotoEvents.onMouseEnter);
    }
    if (collectPhotoEvents.onMouseLeave) {
      map.off('mouseleave', COLLECT_PHOTO_LAYER_ID_POINT, collectPhotoEvents.onMouseLeave);
    }
    if (collectPhotoEvents.onClick) {
      map.off('click', COLLECT_PHOTO_LAYER_ID_POINT, collectPhotoEvents.onClick);
    }
    collectPhotoEvents = {};

    // Remove popup
    const existingPopup = (window as any).__collectPhotoPopup;
    if (existingPopup) {
      existingPopup.remove();
      (window as any).__collectPhotoPopup = null;
    }

    CollectPhotoVectorTileUtils._removeLayers();

    if (map.getSource(COLLECT_PHOTO_SOURCE_ID)) {
      map.removeSource(COLLECT_PHOTO_SOURCE_ID);
    }

    currentProjectId = undefined;
  },

  isLayerVisible: (): boolean => {
    const map = window.map;
    if (!map) return false;
    if (!map.getLayer(COLLECT_PHOTO_LAYER_ID_POINT)) return false;
    const visibility = map.getLayoutProperty(COLLECT_PHOTO_LAYER_ID_POINT, 'visibility');
    return visibility !== 'none';
  },

  setVisible: (visible: boolean) => {
    const map = window.map;
    if (!map) return;
    const vis = visible ? 'visible' : 'none';
    const layerIds = [COLLECT_PHOTO_LAYER_ID_POINT, COLLECT_PHOTO_LAYER_ID_LABEL];
    layerIds.forEach((id) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', vis);
      }
    });
  },

  toggleLayer: () => {
    const isVisible = CollectPhotoVectorTileUtils.isLayerVisible();
    CollectPhotoVectorTileUtils.setVisible(!isVisible);
  },

  refreshData: () => {
    const map = window.map;
    if (!map || !map.getSource(COLLECT_PHOTO_SOURCE_ID)) return;

    try {
      const timestamp = Date.now();
      const tileUrl = currentProjectId
        ? `${API_URL_ADMIN}/api/geocollect/tiles/collect-item-photo/{z}/{x}/{y}.pbf?project_id=${currentProjectId}&_t=${timestamp}`
        : `${API_URL_ADMIN}/api/geocollect/tiles/collect-item-photo/{z}/{x}/{y}.pbf?_t=${timestamp}`;

      const source = map.getSource(COLLECT_PHOTO_SOURCE_ID) as any;
      if (source && source.setTiles) {
        source.setTiles([tileUrl]);
        return;
      }
      if (source && source._options) {
        source._options.tiles = [tileUrl];
        if (source.reload) {
          source.reload();
          return;
        }
      }
    } catch (error) {
      console.error('Error refreshing CollectPhoto tiles:', error);
    }
  },
};

export default CollectPhotoVectorTileUtils;
